import { assertStaffCapability, assertStaffRelationship, } from "./authorization.js";
import { boundedText } from "./bounded-text.js";
import { DomainValidationError } from "./identifiers.js";
import { classifyMemoryValue, memoryRetentionCeilingAt, strongerConfirmationLevel, } from "./memory-taxonomy.js";
import { MEMORY_POLICY_VERSION } from "./memory-policy.js";
import { checkExpectedVersion, denied, isMember, requireReason, violation, } from "./transition-support.js";
// Frozen: the database CHECK constraints are generated from these tuples.
export const memoryFactKinds = Object.freeze([
    "DIETARY_PREFERENCE",
    "DINING_STYLE",
    "PROPERTY_PREFERENCE",
    "SCHEDULE_PREFERENCE",
    "INTEREST",
    "DISLIKED_EXPERIENCE",
    "TRAVEL_COMPANION_PATTERN",
    "EXPLICIT_INSTRUCTION",
]);
/** `DELETED` is the tombstone: the value columns are dropped, the provenance and audit stay. */
export const memoryFactStatuses = Object.freeze([
    "CANDIDATE",
    "ACTIVE",
    "DISPUTED",
    "SUPERSEDED",
    "EXPIRED",
    "DELETED",
]);
export const memorySourceTypes = Object.freeze([
    "EXPLICIT_CUSTOMER_STATEMENT",
    "EXPLICIT_REMEMBER_REQUEST",
    "STAFF_ENTRY",
    "APPROVED_EXTRACTION",
    "HISTORICAL_TRIP_OUTCOME",
    "SYSTEM_DERIVED_OBSERVATION",
]);
/** Sources that are inferred, not said: each needs a source reference (provenance). */
export const memoryInferredSourceTypes = Object.freeze([
    "APPROVED_EXTRACTION",
    "HISTORICAL_TRIP_OUTCOME",
    "SYSTEM_DERIVED_OBSERVATION",
]);
export const memorySourceReferenceKinds = Object.freeze([
    "CONVERSATION_MESSAGE",
    "CONCIERGE_REQUEST",
    "TRIP",
    "EXTRACTION_RUN",
    "CUSTOMER_APP_ENTRY",
    "STAFF_CONSOLE_ENTRY",
]);
export const memorySensitivities = Object.freeze(["STANDARD", "PERSONAL", "SENSITIVE"]);
export const memoryCommands = Object.freeze([
    "activate",
    "dispute",
    "supersede",
    "resolve_keep",
    "expire",
    "delete",
]);
const rule = (id, from, to, command, who) => Object.freeze({
    id,
    from,
    to,
    command,
    customer: who.customer,
    staff: who.staff,
    system: Object.freeze([...(who.system ?? [])]),
});
const APP = { customer: "APP", staff: true };
const ANY = { customer: "ANY", staff: true };
const CLOCK = { customer: "NONE", staff: false, system: ["memory-expiry"] };
/**
 * The thirteen allowed transitions. M5 and M9 (SUPERSEDED) only happen inside a correction, which
 * inserts the successor; there is no resurrection (a reconfirmed preference is a new fact). The
 * database guard (LF015) allows exactly these (from, to) pairs.
 */
export const memoryTransitions = Object.freeze([
    rule("M1", "CANDIDATE", "ACTIVE", "activate", APP),
    rule("M2", "CANDIDATE", "DELETED", "delete", ANY),
    rule("M3", "CANDIDATE", "EXPIRED", "expire", CLOCK),
    rule("M4", "ACTIVE", "DISPUTED", "dispute", APP),
    rule("M5", "ACTIVE", "SUPERSEDED", "supersede", APP),
    rule("M6", "ACTIVE", "EXPIRED", "expire", CLOCK),
    rule("M7", "ACTIVE", "DELETED", "delete", ANY),
    rule("M8", "DISPUTED", "ACTIVE", "resolve_keep", APP),
    rule("M9", "DISPUTED", "SUPERSEDED", "supersede", APP),
    rule("M10", "DISPUTED", "EXPIRED", "expire", CLOCK),
    rule("M11", "DISPUTED", "DELETED", "delete", ANY),
    rule("M12", "SUPERSEDED", "DELETED", "delete", ANY),
    rule("M13", "EXPIRED", "DELETED", "delete", ANY),
]);
export function isAllowedMemoryTransition(from, to) {
    return memoryTransitions.some((row) => row.from === from && row.to === to);
}
const AUDIT = {
    activate: ["customer_memory_fact.activated", "customer_memory_fact.activated_by_staff"],
    dispute: ["customer_memory_fact.disputed", "customer_memory_fact.disputed_by_staff"],
    resolve_keep: [
        "customer_memory_fact.dispute_resolved",
        "customer_memory_fact.dispute_resolved_by_staff",
    ],
    delete: ["customer_memory_fact.deleted", "customer_memory_fact.deleted_by_staff"],
    expire: ["customer_memory_fact.expired", null],
};
/**
 * Standing only: a customer must own the fact; staff need `memory.manage` AND an effective assignment to the customer (or
 * the supervisor role). It runs FIRST in every planner, before any state check, so a staff member with no relationship gets
 * the one refusal (`relationship_missing`) whatever the fact's status: a tombstone, a closed fact and a live one look alike.
 */
function assertMemoryActor(fact, actor, environment) {
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== fact.customerId)
                throw denied("not_owner");
            return;
        case "staff":
            assertStaffCapability(actor, "memory.manage", environment);
            assertStaffRelationship(actor.authority, actor.relationship, "CUSTOMER_DATA");
            return;
        case "system":
            return;
    }
}
const customerMayDrive = (mode, channel) => mode === "ANY" || (mode === "APP" && channel === "CUSTOMER_APP");
/** The confirmation level an actor gives a fact by acting on it. A system actor confirms nothing. */
const confirmationLevelOf = (actor) => actor.kind === "customer"
    ? "CUSTOMER_CONFIRMED"
    : actor.kind === "staff"
        ? "STAFF_CONFIRMED"
        : "UNCONFIRMED";
/** An expiry only moves forward: the later ceiling replaces it, and an expiry already past the ceiling (a legacy row) stays. */
function extendedExpiry(current, ceilingAt) {
    return current !== null && current.getTime() >= ceilingAt.getTime() ? current : ceilingAt;
}
/**
 * Stale-write protection (design 5.4). Staff and the customer in the app must name the version they read.
 * The agent channel (which may only delete) and the expiry sweep may omit it. A wrong version is
 * `memory.version_conflict`.
 */
export function checkMemoryExpectedVersion(actor, expectedVersion, currentVersion) {
    if (expectedVersion === null && actor.kind === "customer" && actor.channel === "CUSTOMER_APP") {
        throw new DomainValidationError("expectedVersion", "is required");
    }
    checkExpectedVersion(actor, expectedVersion, currentVersion, "memory.version_conflict");
}
/**
 * Plans one of the eleven transitions that stand alone, or refuses it. Checks run in this order:
 * (1) the actor's standing (for staff: the capability, then the relationship to the customer), (2) the state (`memory.tombstoned` from DELETED, else
 * `memory.transition_invalid`; supersession is only part of a correction), (3) the actor against
 * the rule (the agent channel may delete but never activate, dispute or resolve; only the expiry
 * policy expires), (4) `expectedVersion`, (5) the staff reason, then (6) for an expiry, that the
 * fact really has expired. M1 (activate) stamps a fresh expiry and the actor's confirmation level; M8
 * stamps the level. A staff rejection of a candidate is the same M2 with its own audit action. Nothing
 * is written here, and no message ever contains a value.
 */
export function planMemoryTransition(input) {
    const { fact, to, command, actor, now } = input;
    assertMemoryActor(fact, actor, input.environment);
    if (fact.status === "DELETED")
        throw violation("memory.tombstoned");
    const matched = memoryTransitions.find((row) => row.from === fact.status && row.to === to);
    if (matched === undefined || matched.command !== command || command === "supersede") {
        throw violation("memory.transition_invalid");
    }
    switch (actor.kind) {
        case "customer":
            if (!customerMayDrive(matched.customer, actor.channel))
                throw denied("role_missing");
            break;
        case "system":
            if (!matched.system.includes(actor.actorId))
                throw denied("role_missing");
            break;
        case "staff":
            if (!matched.staff)
                throw denied("role_missing");
            break;
    }
    checkMemoryExpectedVersion(actor, input.expectedVersion, fact.version);
    if (actor.kind === "staff")
        requireReason(input.reason);
    if (command === "expire") {
        if (fact.expiresAt === null || fact.expiresAt.getTime() > now.getTime()) {
            throw violation("memory.not_expired_yet");
        }
    }
    if (input.candidateRejection === true &&
        (actor.kind !== "staff" || fact.status !== "CANDIDATE")) {
        throw violation("memory.transition_invalid");
    }
    const [customerOrSystemAction, staffAction] = AUDIT[command];
    let patch;
    if (to === "DELETED") {
        patch = {
            kind: "tombstone",
            status: "DELETED",
            rawValue: null,
            normalizedValue: null,
            deletedAt: "now",
        };
    }
    else if (command === "activate") {
        patch = {
            kind: "activate",
            status: "ACTIVE",
            expiresAt: extendedExpiry(fact.expiresAt, memoryRetentionCeilingAt(fact.kind, "ACTIVE", now)),
            confirmationLevel: strongerConfirmationLevel(fact.confirmationLevel, confirmationLevelOf(actor)),
        };
    }
    else if (command === "resolve_keep") {
        patch = {
            kind: "resolve_keep",
            status: "ACTIVE",
            confirmationLevel: strongerConfirmationLevel(fact.confirmationLevel, confirmationLevelOf(actor)),
        };
    }
    else {
        patch = { kind: "set_status", status: to };
    }
    return Object.freeze({
        ruleId: matched.id,
        command,
        from: fact.status,
        to,
        patch,
        auditAction: input.candidateRejection === true
            ? "customer_memory_fact.candidate_rejected_by_staff"
            : actor.kind === "staff" && staffAction !== null
                ? staffAction
                : customerOrSystemAction,
    });
}
/**
 * Re-confirms an ACTIVE fact: it stamps `last_confirmed_at`, moves the expiry forward to the retention
 * ceiling and raises the confirmation level to the actor's. Only the app (never the agent) or staff may
 * confirm, the stamp only moves forward, and a fact that is not ACTIVE cannot be confirmed. A
 * confirmation is the only way an expiry grows besides activation: the extraction job never extends one.
 */
export function planMemoryConfirm(input) {
    const { fact, actor } = input;
    assertMemoryActor(fact, actor, input.environment);
    if (fact.status === "DELETED")
        throw violation("memory.tombstoned");
    if (fact.status !== "ACTIVE")
        throw violation("memory.transition_invalid");
    switch (actor.kind) {
        case "customer":
            if (!customerMayDrive("APP", actor.channel))
                throw denied("role_missing");
            break;
        case "system":
            throw denied("role_missing");
        case "staff":
            break;
    }
    checkMemoryExpectedVersion(actor, input.expectedVersion, fact.version);
    if (actor.kind === "staff")
        requireReason(input.reason);
    if (fact.lastConfirmedAt !== null && fact.lastConfirmedAt.getTime() > input.now.getTime()) {
        throw violation("memory.transition_invalid");
    }
    return Object.freeze({
        patch: {
            kind: "confirm",
            lastConfirmedAt: "now",
            expiresAt: extendedExpiry(fact.expiresAt, memoryRetentionCeilingAt(fact.kind, "ACTIVE", input.now)),
            confirmationLevel: strongerConfirmationLevel(fact.confirmationLevel, confirmationLevelOf(actor)),
        },
        auditAction: actor.kind === "staff"
            ? "customer_memory_fact.confirmed_by_staff"
            : "customer_memory_fact.confirmed",
    });
}
const REFERENCE_ID = /^[A-Za-z0-9:_.-]{1,200}$/;
const CUSTOMER_SOURCES = [
    "EXPLICIT_CUSTOMER_STATEMENT",
    "EXPLICIT_REMEMBER_REQUEST",
];
const STAFF_SOURCES = ["STAFF_ENTRY", "HISTORICAL_TRIP_OUTCOME"];
const fail = (field, rule) => new DomainValidationError(field, rule);
const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());
/**
 * Validates the shape of a fact a customer or a staff member wants to write: bounded values, a value the
 * kind accepts (classified by the Phase 5 taxonomy, which also gives the sensitivity), a source the actor
 * may have, provenance for an inferred source, an expiry after the observation and within the retention
 * ceiling, and the starting status (a customer in the app writes ACTIVE and is customer-confirmed; the
 * agent channel writes CANDIDATE only, so a model cannot promote anything; staff choose ACTIVE or CANDIDATE
 * and give a reason). A system actor does not use this function: extraction goes through
 * `planMemoryExtractionInsert`. The messages name fields and rules, never a value.
 */
export function validateMemoryFactInput(input, actor, reason, environment, now) {
    switch (actor.kind) {
        case "customer":
            if (actor.customerId !== input.customerId)
                throw denied("not_owner");
            break;
        case "staff":
            assertStaffCapability(actor, "memory.manage", environment);
            assertStaffRelationship(actor.authority, actor.relationship, "CUSTOMER_DATA");
            break;
        case "system":
            // Extraction writes through planMemoryExtractionInsert: no other system actor writes a fact.
            throw denied("role_missing");
    }
    if (!isMember(memoryFactKinds, input.kind))
        throw fail("kind", "must be a known memory kind");
    if (!isMember(memorySourceTypes, input.sourceType)) {
        throw fail("sourceType", "must be a known source type");
    }
    const rawValue = boundedText("rawValue", input.rawValue, 1000, { multiline: true });
    const classified = classifyMemoryValue(input.kind, rawValue);
    if (!classified.ok)
        throw fail("value", classificationRule(classified.reason));
    const confidence = input.confidencePermille ?? null;
    if (confidence !== null &&
        (typeof confidence !== "number" ||
            !Number.isInteger(confidence) ||
            confidence < 0 ||
            confidence > 1000)) {
        throw fail("confidencePermille", "must be a whole number from 0 to 1000");
    }
    let reference = input.sourceReference ?? null;
    if (reference !== null) {
        if (!isMember(memorySourceReferenceKinds, reference.kind)) {
            throw fail("sourceReference.kind", "must be a known reference kind");
        }
        if (typeof reference.id !== "string" || !REFERENCE_ID.test(reference.id)) {
            throw fail("sourceReference.id", "must be an identifier of 1 to 200 characters");
        }
    }
    if (!isValidDate(input.firstObservedAt))
        throw fail("firstObservedAt", "must be a valid date");
    const requested = input.requestedStatus ?? null;
    if (requested !== null && requested !== "ACTIVE" && requested !== "CANDIDATE") {
        throw fail("requestedStatus", "must be ACTIVE or CANDIDATE");
    }
    const allowedSources = actor.kind === "customer" ? CUSTOMER_SOURCES : STAFF_SOURCES;
    if (!allowedSources.includes(input.sourceType))
        throw violation("memory.source_not_allowed");
    if (actor.kind === "staff")
        requireReason(reason);
    let status;
    if (actor.kind === "staff") {
        status = requested ?? "ACTIVE";
    }
    else {
        status = actor.channel === "CUSTOMER_APP" ? "ACTIVE" : "CANDIDATE";
    }
    // A staff entry always carries its console provenance; a historical outcome names the trip or request.
    if (actor.kind === "staff" && input.sourceType === "STAFF_ENTRY") {
        reference ??= { kind: "STAFF_CONSOLE_ENTRY", id: actor.authority.staffUserId };
    }
    if (memoryInferredSourceTypes.includes(input.sourceType) &&
        reference === null) {
        throw violation("memory.provenance_required");
    }
    const ceilingAt = memoryRetentionCeilingAt(input.kind, status, now);
    const expiresAt = input.expiresAt ?? ceilingAt;
    if (!isValidDate(expiresAt))
        throw fail("expiresAt", "must be a valid date");
    if (expiresAt.getTime() <= input.firstObservedAt.getTime()) {
        throw fail("expiresAt", "must be after the fact was first observed");
    }
    if (expiresAt.getTime() <= now.getTime())
        throw fail("expiresAt", "must be in the future");
    if (expiresAt.getTime() > ceilingAt.getTime()) {
        throw fail("expiresAt", "must not be later than the retention ceiling of the kind");
    }
    return Object.freeze({
        customerId: input.customerId,
        kind: input.kind,
        status,
        rawValue,
        normalizedValue: classified.normalizedValue,
        confidencePermille: confidence,
        sourceType: input.sourceType,
        sourceReferenceKind: reference === null ? null : reference.kind,
        sourceReferenceId: reference === null ? null : reference.id,
        sensitivity: classified.sensitivity,
        firstObservedAt: input.firstObservedAt,
        lastConfirmedAt: null,
        expiresAt,
        supersedesFactId: null,
        createdByActorKind: actor.kind,
        createdByActorId: actor.kind === "customer" ? actor.customerId : actor.authority.staffUserId,
        policyVersion: MEMORY_POLICY_VERSION,
        reviewReason: null,
        conflictsWithFactId: null,
        extractionRunId: null,
        confirmationLevel: status !== "ACTIVE"
            ? "UNCONFIRMED"
            : actor.kind === "customer"
                ? "CUSTOMER_CONFIRMED"
                : "STAFF_CONFIRMED",
        auditAction: actor.kind === "staff"
            ? "customer_memory_fact.created_by_staff"
            : "customer_memory_fact.created",
    });
}
function classificationRule(reason) {
    switch (reason) {
        case "OUT_OF_VOCABULARY":
            return "must be a code the memory kind accepts";
        case "TEXT_NOT_ALLOWED":
            return "must be a code: this memory kind takes no free text";
        case "TEXT_TOO_LONG":
            return "must be at most 160 characters";
        case "SENSITIVE_TEXT":
            return "is not stored: it belongs to a sensitive class";
    }
}
/**
 * Plans the insert of one fact for a decision of the write policy (design 8.4, step 10). Only the extraction
 * system writes it. A `CANDIDATE` always carries the run and the message evidence; an `ACTIVE` fact is the
 * narrow auto-promotion: a customer statement (never an inference), no review reason, not a companion
 * pattern, STANDARD or from an explicit remember request. The database guard LF015 holds the same floor.
 */
export function planMemoryExtractionInsert(input) {
    const { insert, actor } = input;
    if (actor.kind !== "system" || actor.actorId !== "memory-extraction") {
        throw denied("role_missing");
    }
    if (!REFERENCE_ID.test(input.sourceMessageId)) {
        throw fail("sourceReference.id", "must be an identifier of 1 to 200 characters");
    }
    if (!isValidDate(input.observedAt))
        throw fail("firstObservedAt", "must be a valid date");
    const confidence = input.confidencePermille;
    if (confidence !== null &&
        (!Number.isInteger(confidence) || confidence < 0 || confidence > 1000)) {
        throw fail("confidencePermille", "must be a whole number from 0 to 1000");
    }
    if (insert.status === "ACTIVE") {
        const stated = insert.sourceType === "EXPLICIT_CUSTOMER_STATEMENT" ||
            insert.sourceType === "EXPLICIT_REMEMBER_REQUEST";
        if (!stated ||
            insert.reviewReason !== null ||
            insert.kind === "TRAVEL_COMPANION_PATTERN" ||
            (insert.sensitivity !== "STANDARD" && insert.sourceType !== "EXPLICIT_REMEMBER_REQUEST")) {
            throw violation("memory.source_not_allowed");
        }
    }
    const ceilingAt = memoryRetentionCeilingAt(insert.kind, insert.status, input.now);
    if (ceilingAt.getTime() <= input.observedAt.getTime()) {
        throw fail("expiresAt", "must be after the fact was first observed");
    }
    return Object.freeze({
        customerId: input.customerId,
        kind: insert.kind,
        status: insert.status,
        rawValue: insert.rawValue,
        normalizedValue: insert.normalizedValue,
        confidencePermille: confidence,
        sourceType: insert.sourceType,
        sourceReferenceKind: "CONVERSATION_MESSAGE",
        sourceReferenceId: input.sourceMessageId,
        sensitivity: insert.sensitivity,
        firstObservedAt: input.observedAt,
        lastConfirmedAt: null,
        expiresAt: ceilingAt,
        supersedesFactId: null,
        createdByActorKind: "system",
        createdByActorId: actor.actorId,
        policyVersion: MEMORY_POLICY_VERSION,
        reviewReason: insert.reviewReason,
        conflictsWithFactId: insert.conflictsWithFactId,
        extractionRunId: input.extractionRunId,
        confirmationLevel: "UNCONFIRMED",
        auditAction: insert.status === "ACTIVE"
            ? "customer_memory_fact.auto_activated"
            : "customer_memory_fact.candidate_proposed",
    });
}
/**
 * A correction never edits a value: it marks the base SUPERSEDED (M5 from ACTIVE, M9 from DISPUTED)
 * and inserts an ACTIVE successor of the same kind for the same customer that links the base, so the
 * evidence of what was said before stays. The successor is a new fact: a fresh expiry, and the
 * confirmation level of the person who corrected. Only the app (never the agent) or staff correct.
 */
export function planMemoryCorrection(input) {
    const { base, actor } = input;
    assertMemoryActor(base, actor, input.environment);
    if (actor.kind === "system")
        throw denied("role_missing");
    if (base.status === "DELETED")
        throw violation("memory.tombstoned");
    if (base.status !== "ACTIVE" && base.status !== "DISPUTED") {
        throw violation("memory.transition_invalid");
    }
    if (actor.kind === "customer") {
        if (!customerMayDrive("APP", actor.channel))
            throw denied("role_missing");
    }
    checkMemoryExpectedVersion(actor, input.expectedVersion, base.version);
    if (actor.kind === "staff")
        requireReason(input.reason);
    if (input.input.kind !== base.kind)
        throw fail("kind", "must equal the corrected fact's kind");
    if (input.input.customerId !== base.customerId)
        throw fail("customerId", "must be the fact's customer");
    if (input.input.requestedStatus !== undefined &&
        input.input.requestedStatus !== null &&
        input.input.requestedStatus !== "ACTIVE") {
        throw fail("requestedStatus", "a correction is ACTIVE");
    }
    const validated = validateMemoryFactInput(input.input, actor, input.reason, input.environment, input.now);
    return Object.freeze({
        ruleId: base.status === "ACTIVE" ? "M5" : "M9",
        from: base.status,
        basePatch: { kind: "set_status", status: "SUPERSEDED" },
        successor: Object.freeze({
            ...validated,
            status: "ACTIVE",
            supersedesFactId: base.id,
            confirmationLevel: confirmationLevelOf(actor),
        }),
        auditAction: actor.kind === "staff"
            ? "customer_memory_fact.corrected_by_staff"
            : "customer_memory_fact.corrected",
    });
}
/** Normal retrieval: ACTIVE and not expired. CANDIDATE, DISPUTED, SUPERSEDED, EXPIRED and DELETED never qualify. */
export function isRetrievable(fact, now) {
    return (fact.status === "ACTIVE" &&
        (fact.expiresAt === null || fact.expiresAt.getTime() > now.getTime()));
}
